import React from 'react';
import SignerContext from 'hellospa/components/signer-signature-document/context';
import SignerDocument from 'hellospa/components/signer-signature-document/document';
import SignatureProviderIntercept from 'common/signature-modal/signature-provider-intercept';
import { PREPARER, SENDER } from '../../signature-request';
import { Field, FieldTypes, Page, Signer } from '../../editor/types';
import { Rule } from 'hellospa/conditional-logic/types';
import { selectPreviewCurrentSigner, selectPreviewFields, selectPreviewSignerOrderEnabled, selectPreviewSigners } from './data/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { initPreview, updatePreviewField } from './data/actions';
import { SignatureRequest } from '../types';
import { trackHeapCustomEvent } from 'common/utils/heap';

function useFilteredSignatureRequest(
  signatureRequest: SignatureRequest,
): SignatureRequest {
  const signers = useSelector(selectPreviewSigners);
  const currentSigner = useSelector(selectPreviewCurrentSigner);
  const signerOrderEnabled = useSelector(selectPreviewSignerOrderEnabled);

  // I'd prefer to put this in a selector, but we don't have tools in place yet
  // to cache selector results
  const fields = React.useMemo(() => {
    const stampField = (field: Field): Field[] => {
      if (field.type === FieldTypes.Checkbox && !field.checked) {
        // All PREPARER checkboxes are checked automatically, so if this
        // isn't checked it needs to be removed.
        return [];
      }

      return [{
        ...field,
        // Converting to PREPARER removes the color coding to make them look
        // stamped into the doc, just like a signer would see.
        signer: PREPARER,
        readOnly: true,
      }];
    };

    if (currentSigner) {
      return signatureRequest.fields.flatMap((field) => {
        // The current signer's fields are always returned unmodified
        if (String(field.signer) === String(currentSigner.id)) {
          return [field];
        }

        if (field.signer === PREPARER || field.signer === SENDER) {
          return [field];
        } else if (!signerOrderEnabled) {
          // If there is no signer order, hide all other signers
          return [];
        } else {
          if (currentSigner.order === undefined) {
            throw new Error(`missing signer order for: ${currentSigner.id}`);
          }

          const signerForField = signers.find((s) => String(s.id) === String(field.signer));
          if (!signerForField) {
            throw new Error(`Signer not found: ${field.signer}`);
          }
          if (signerForField.order === undefined) {
            throw new Error(`missing signer order for: ${signerForField.id}`);
          }

          // Remove all fields assigned to future signers
          if (signerForField.order > currentSigner.order) {
            return [];
          }
          return stampField(field);
        }
      });
    }
    return signatureRequest.fields;
  }, [currentSigner, signatureRequest.fields, signerOrderEnabled, signers]);

  const rulesWithFilteredTriggers = React.useMemo(() => {
    const fieldIds = fields.map((field) => field.id);
    return signatureRequest.rules.flatMap((rule) => {
      const filteredTriggers = rule.triggers.filter((trigger) => {
        return fieldIds.includes(trigger.id);
      });

      if (filteredTriggers.length > 0) {
        return [{ ...rule, triggers: filteredTriggers }];
      }
      return [];
    });
  }, [fields, signatureRequest.rules]);

  return React.useMemo(() => ({
    ...signatureRequest,
    fields,
    rules: rulesWithFilteredTriggers,
  }), [fields, rulesWithFilteredTriggers, signatureRequest]);
}

type Props = {
  signers: Signer[],
  signerOrderEnabled: boolean,
  pages: Page[],
  fields: Field[],
  rules: Rule[],
  dateFormat: string,
}
export default function DocumentPreview(props: Props) {
  const { pages, dateFormat, rules } = props;
  const dispatch = useDispatch();
  const validationErrors = {};
  const startEnabled = true;
  const fields = useSelector(selectPreviewFields);
  const currentSigner = useSelector(selectPreviewCurrentSigner);

  const onFieldUpdate = React.useCallback((field: Field, updates: Partial<Field>) => {
    dispatch(updatePreviewField(field.id, updates));
    trackHeapCustomEvent('Preview Field Edit', {
      'Field Type': field.type,
    });
  }, [dispatch]);

  // The user can leave DocumentPreview, edit fields/rules/signers and return.
  // So it needs to be reinitialized every time it mounts.
  const [hasInitialized, setInitialized] = React.useState(false);
  React.useEffect(() => {
    if (!hasInitialized) {
      dispatch(initPreview({
        signers: props.signers,
        signerOrderEnabled: props.signerOrderEnabled,
        fields: props.fields,
        rules: props.rules,
      }));
      setInitialized(true);
    }
    // eslint-disable-next-line max-len
  }, [dispatch, hasInitialized, props.fields, props.rules, props.signerOrderEnabled, props.signers]);

  const [currentFieldId, setCurrentField] = React.useState<string | null>();
  const selectedFieldIds = React.useMemo(
    () => (currentFieldId != null ? [currentFieldId] : []),
    [currentFieldId],
  );

  const tmpRequest = React.useMemo(() => {
    return {
      fields: fields.map((field) => {
        if (field.signer === PREPARER) {
          return {
            ...field,
            readOnly: true,
          };
        } else if (field.signer === SENDER) {
          return {
            ...field,
            // This hides the asterisk in the preview
            required: false,
          };
        }

        return field;
      }),
      pages,
      rules,
      dpi: 80,
    };
  }, [fields, pages, rules]);
  const signatureRequest = useFilteredSignatureRequest(tmpRequest);

  const getInstantValidationErrorHack = React.useCallback(() => { }, []);

  const fieldResetKey = React.useMemo(
    () => String(Math.random()),
    // Generate a new key any time the signer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSigner],
  );
  if (!hasInitialized) {
    return null;
  }
  return (
    <SignerContext
      fieldResetKey={fieldResetKey}
      documentPreview={true}
      validationErrors={validationErrors}
      startEnabled={startEnabled}
      onFieldUpdate={onFieldUpdate}
      selectedFieldIds={selectedFieldIds}
      setCurrentField={(id) => setCurrentField(id)}
      signatureRequest={signatureRequest}
      defaultDateFormat={dateFormat}
      getInstantValidationErrorHack={getInstantValidationErrorHack}
    >
      {/*
        This intercept is where temporary signatures/initials get stored.
        Before I added fieldResetKey, I tried to use `<SignerContext key=`,
        but that caused signatures to be lost when changing between signers.
       */}
      <SignatureProviderIntercept>
        <SignerDocument />
      </SignatureProviderIntercept>
    </SignerContext>
  );
}
